import * as React from "react";
import {
  Box,
  Button,
  ChakraProps,
  HStack,
  IconButton,
  Image,
  ImageProps,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VisuallyHidden,
  VStack
} from "@chakra-ui/react";
import { IoTrashBinOutline } from "react-icons/io5";
import logo from "assets/logo.png";
import {
  convertMbToBytes,
  generateId,
  getBase64,
  getImageUrl,
  ImageType,
  useField
} from "duck";
import Modal from "../../../modal";
import { Types } from "../../duck";

export interface ImageUploaderProps
  extends Omit<Types.BaseInputProps, "inputLayoutProps"> {
  maxSizeMB?: number;
  allowedTypes?: string[];
  onChange?(file: null | ImageType): Promise<any> | void;
  value?: number | ImageType | null;
  error?: React.ReactNode;
  bordered?: boolean;
  boxProps?: ChakraProps;
  imageProps?: ImageProps;
  stretch?: boolean;
  enableZoom?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  name,
  maxSizeMB = 10,
  label,
  labelHidden,
  allowedTypes = ["jpg", "jpeg", "png", "svg"],
  onChange: customOnChange,
  value: customValue,
  error: customError,
  boxProps,
  stretch,
  imageProps,
  enableZoom = false
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const formProps = useField(name);

  const onChange =
    formProps.onChange === undefined
      ? customOnChange || (() => {})
      : formProps.onChange;

  const value: ImageType | number | null =
    formProps.value === undefined ? customValue : formProps.value;
  const isBase64Value = !!(value as ImageType)?.base64Format;

  const validationError = error || formProps.error || customError;

  const onImageChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    const file = event?.target?.files?.[0];

    if (!file) {
      return;
    }

    const sizeValid = file.size < convertMbToBytes(maxSizeMB);
    const typeValid = allowedTypes.some(type => file.type.includes(type));
    const isValid = sizeValid && typeValid;

    if (isValid) {
      setError(null);
      setLoading(true);

      getBase64(file)
        .then(base64 => {
          if (base64) {
            const onChangeResult = onChange({
              base64Format: base64,
              name: file.name
            });
            if (onChangeResult) {
              return onChangeResult.finally(() => setLoading(false));
            }
            setLoading(false);
          } else {
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
      return;
    }

    setError(
      typeValid
        ? `Image size is too large. Max image size is ${maxSizeMB}MB`
        : `Image format is wrong. Allowed file formats: ${allowedTypes.join(
            ", "
          )}.`
    );
  };

  const htmlFor = React.useMemo(() => name || generateId(), [name]);

  return (
    <>
      {label && labelHidden && <VisuallyHidden>{label}</VisuallyHidden>}
      {label && !labelHidden && (
        <Text mb={2} fontSize="md" fontWeight="500">
          {label}
        </Text>
      )}
      <input
        accept={allowedTypes.map(fileType => `.${fileType}`).join(", ")}
        id={htmlFor}
        type="file"
        style={{ display: "none" }}
        onChange={onImageChange}
        ref={inputRef}
      />
      <HStack align="top" w={stretch ? "100%" : undefined}>
        <Tooltip
          openDelay={400}
          label={
            <>
              <Text fontSize="sm">Formats: {allowedTypes?.join(", ")}</Text>
              <Text fontSize="sm">Size: max size {maxSizeMB}MB</Text>
            </>
          }
          placement="auto"
        >
          <VStack gap={2} w={stretch ? "100%" : undefined}>
            <Box
              w={stretch ? "100%" : 120}
              h={120}
              display="flex"
              justifyContent="center"
              alignItems="center"
              border="2px dashed"
              borderColor="gray.200"
              _hover={{ cursor: "pointer", borderStyle: "solid" }}
              onClick={() => {
                if (value && enableZoom) {
                  onOpen();
                }
              }}
              {...boxProps}
            >
              {loading ? (
                <Spinner />
              ) : (
                <>
                  {value ? (
                    <Image
                      w={stretch ? undefined : 120}
                      h={stretch ? "100%" : 120}
                      objectFit="cover"
                      {...imageProps}
                      src={
                        isBase64Value
                          ? (value as ImageType).base64Format
                          : getImageUrl(value as number)
                      }
                    />
                  ) : (
                    <Image
                      src={logo}
                      w="78px"
                      h="30px"
                      verticalAlign="middle"
                    />
                  )}
                </>
              )}
            </Box>
            <HStack>
              <Button
                disabled={loading}
                cursor="pointer"
                size="sm"
                as="label"
                htmlFor={htmlFor}
              >
                Upload
              </Button>
              {!loading && value && (
                <IconButton
                  variant="outline"
                  aria-label="Delete image"
                  colorScheme="red"
                  size="sm"
                  icon={<IoTrashBinOutline />}
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.value = "";
                    }
                    onChange(null);
                  }}
                />
              )}
            </HStack>
          </VStack>
        </Tooltip>
      </HStack>
      {validationError ? (
        <HStack
          w={stretch ? "100%" : "120px"}
          justify={stretch ? "center" : "start"}
        >
          <Text color="red.500" fontSize="sm" mt={2} whiteSpace="nowrap">
            {validationError as string}
          </Text>
        </HStack>
      ) : null}
      <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
        <Box pt={6} pb={2}>
          <Image
            w="full"
            src={
              isBase64Value
                ? (value as ImageType).base64Format
                : getImageUrl(value as number)
            }
            alt="Image"
          />
        </Box>
      </Modal>
    </>
  );
};

export default ImageUploader;
